import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Download, 
  Monitor, 
  Server, 
  Apple, 
  Shield, 
  Copy, 
  Check,
  Terminal,
  ChevronRight,
  AlertCircle,
  Cpu,
  HardDrive,
  PlayCircle,
  Settings
} from 'lucide-react';
import api from '../lib/api';

const AgentDownload = () => {
  const navigate = useNavigate();
  const [agentKey, setAgentKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedCmd, setCopiedCmd] = useState(false);
  const [selectedOS, setSelectedOS] = useState('windows');
  const [selectedArch, setSelectedArch] = useState('amd64');
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('shirosec_token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetchAgentKey();
  }, [navigate]);

  const fetchAgentKey = async () => {
    try {
      const response = await api.get('/agent/download-key');
      setAgentKey(response.data.agent_key);
    } catch (error) {
      console.error('Failed to get agent key:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type = 'key') => {
    navigator.clipboard.writeText(text);
    if (type === 'key') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setCopiedCmd(true);
      setTimeout(() => setCopiedCmd(false), 2000);
    }
  };

  const platforms = {
    windows: {
      name: 'Windows',
      icon: Monitor,
      architectures: [
        { id: 'amd64', name: 'x64 (Intel/AMD)', filename: 'ShiroAgentInstaller.cmd', size: '12 KB' }
      ],
      versions: ['Windows 10', 'Windows 11'],
      isGuiInstaller: true,
      requiresPython: false,
      instructions: [
        'Download ShiroAgentInstaller.cmd',
        'Double-click the downloaded file',
        'Click "Yes" when Windows asks for permission',
        'Enter your Agent Key and click "Install & Protect"'
      ]
    },
    linux: {
      name: 'Linux',
      icon: Server,
      architectures: [
        { id: 'amd64', name: 'x64 / ARM64 (auto-detected)', filename: 'ShiroAgent-Linux-Installer.sh', size: '3 KB' }
      ],
      versions: ['Ubuntu/Debian', 'RHEL/CentOS', 'Fedora', 'Amazon Linux'],
      isGuiInstaller: false,
      requiresPython: false,
      instructions: [
        'Download ShiroAgent-Linux-Installer.sh',
        'Open Terminal and run: chmod +x ShiroAgent-Linux-Installer.sh',
        'Run with sudo: sudo ./ShiroAgent-Linux-Installer.sh',
        'Enter your Agent Key when prompted'
      ]
    },
    macos: {
      name: 'macOS',
      icon: Apple,
      architectures: [
        { id: 'universal', name: 'Apple Silicon & Intel (auto-detected)', filename: 'ShiroAgent-macOS-Installer.command', size: '4 KB' }
      ],
      versions: ['macOS Ventura', 'macOS Sonoma', 'macOS Sequoia'],
      isGuiInstaller: false,
      requiresPython: false,
      instructions: [
        'Download ShiroAgent-macOS-Installer.command',
        'Double-click to run (or right-click → Open if blocked)',
        'Enter your Agent Key in the Terminal window',
        'Allow in System Settings → Privacy & Security if prompted'
      ]
    }
  };

  const currentPlatform = platforms[selectedOS];
  const currentArch = currentPlatform.architectures.find(a => a.id === selectedArch) || currentPlatform.architectures[0];
  const serverUrl = window.location.origin;
  
  // For Linux command line install
  const registerCommand = currentPlatform.installSteps 
    ? `${currentPlatform.installSteps[selectedArch]} -register -server ${serverUrl} -key ${agentKey}`
    : '';
  const installServiceCommand = currentPlatform.installSteps
    ? `sudo ${currentPlatform.installSteps[selectedArch].replace('sudo ', '')} -install`
    : '';

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-cyan-400" />
              <span className="text-xl font-bold">
                <span className="text-cyan-400">SHIRO</span> Security
              </span>
            </div>
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Download Shiro Agent</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Install the lightweight Shiro Agent on your devices for real-time threat monitoring 
            and AI-powered protection.
          </p>
        </div>

        {/* Agent Key Section */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Your Agent Key</h2>
              <p className="text-sm text-gray-400">Use this key when registering your agent</p>
            </div>
          </div>
          
          {loading ? (
            <div className="bg-gray-800 rounded-lg p-4 animate-pulse">
              <div className="h-6 bg-gray-700 rounded w-3/4"></div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <code className="flex-1 bg-gray-800 rounded-lg px-4 py-3 font-mono text-cyan-400 text-sm overflow-x-auto">
                {agentKey}
              </code>
              <button
                onClick={() => copyToClipboard(agentKey, 'key')}
                className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copied ? <Check className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          )}
          
          <div className="mt-3 flex items-center gap-2 text-sm text-amber-400">
            <AlertCircle className="h-4 w-4" />
            <span>This key expires in 24 hours. Keep it secure.</span>
          </div>
        </div>

        {/* Platform Selection */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Select Your Platform</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(platforms).map(([key, platform]) => {
              const Icon = platform.icon;
              const isSelected = selectedOS === key;
              
              return (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedOS(key);
                    setSelectedArch(platform.architectures[0].id);
                  }}
                  className={`
                    relative p-6 rounded-xl border-2 transition-all text-left
                    ${isSelected 
                      ? 'border-cyan-500 bg-cyan-500/10' 
                      : 'border-gray-800 bg-gray-900 hover:border-gray-700'
                    }
                  `}
                >
                  <Icon className={`h-8 w-8 mb-3 ${isSelected ? 'text-cyan-400' : 'text-gray-400'}`} />
                  <h3 className="font-semibold text-lg mb-1">{platform.name}</h3>
                  <div className="text-xs text-gray-500">
                    {platform.versions.slice(0, 2).join(' • ')}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Architecture Selection */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Cpu className="h-5 w-5 text-purple-400" />
            Select Architecture
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentPlatform.architectures.map((arch) => (
              <button
                key={arch.id}
                onClick={() => setSelectedArch(arch.id)}
                className={`
                  p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4
                  ${selectedArch === arch.id
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-gray-800 bg-gray-900 hover:border-gray-700'
                  }
                `}
              >
                <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                  selectedArch === arch.id ? 'bg-purple-500/20' : 'bg-gray-800'
                }`}>
                  <HardDrive className={`h-6 w-6 ${selectedArch === arch.id ? 'text-purple-400' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h4 className="font-semibold">{arch.name}</h4>
                  <p className="text-sm text-gray-400">{arch.size}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Download & Install Instructions */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          {/* Download Button */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex flex-wrap items-center gap-4">
              <a
                href={`/downloads/${currentArch.filename}`}
                download
                className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 
                         hover:from-cyan-400 hover:to-blue-500 rounded-lg font-semibold transition-all"
              >
                <Download className="h-5 w-5" />
                Download for {currentPlatform.name}
              </a>
              <div className="text-gray-400">
                <span className="font-mono text-sm">{currentArch.filename}</span>
                <span className="text-gray-600 mx-2">•</span>
                <span className="text-sm">{currentArch.size}</span>
              </div>
            </div>
            {currentPlatform.requiresPython && (
              <p className="text-sm text-amber-400 mt-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Requires Python 3.8+ installed on your system
              </p>
            )}
          </div>

          {/* Installation Steps */}
          <div className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <PlayCircle className="h-5 w-5 text-cyan-400" />
              Installation Steps
            </h3>
            
            <div className="space-y-4">
              {currentPlatform.instructions?.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-gray-300">{step}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Agent Key Section */}
            <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <p className="font-medium mb-2">Your Agent Key:</p>
              <div className="flex items-center justify-between gap-3">
                <code className="text-cyan-400 font-mono text-sm break-all flex-1">{agentKey}</code>
                <button
                  onClick={() => copyToClipboard(agentKey, 'key')}
                  className="flex-shrink-0 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded transition-colors flex items-center gap-2"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">This key expires in 24 hours</p>
            </div>

            {/* Success indicator */}
            <div className="mt-6 flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold">
                ✓
              </div>
              <div>
                <p className="font-medium text-green-400">You'll be protected!</p>
                <p className="text-sm text-gray-400">
                  Once installed, the agent will appear in your dashboard with a system tray icon on your computer.
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="p-6 bg-gray-800/50 border-t border-gray-800">
            <h4 className="font-medium mb-3">What the agent monitors:</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[
                'Process Activity',
                'Network Connections',
                'File Integrity',
                'Resource Usage',
                'Login Events',
                'System Logs'
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-gray-300">
                  <ChevronRight className="h-4 w-4 text-cyan-400" />
                  {feature}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* All Downloads Table */}
        <div className="mt-8 bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-semibold">All Available Downloads</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-400">Platform</th>
                  <th className="text-left p-4 font-medium text-gray-400">Architecture</th>
                  <th className="text-left p-4 font-medium text-gray-400">Size</th>
                  <th className="text-left p-4 font-medium text-gray-400">Download</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {Object.entries(platforms).map(([osKey, platform]) => 
                  platform.architectures.map((arch, idx) => (
                    <tr key={`${osKey}-${arch.id}`} className="hover:bg-gray-800/30">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <platform.icon className="h-4 w-4 text-gray-400" />
                          {idx === 0 ? platform.name : ''}
                        </div>
                      </td>
                      <td className="p-4 text-gray-300">{arch.name}</td>
                      <td className="p-4 text-gray-400">{arch.size}</td>
                      <td className="p-4">
                        <a
                          href={`/downloads/${arch.filename}`}
                          download
                          className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300"
                        >
                          <Download className="h-4 w-4" />
                          {arch.filename}
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 text-center text-gray-400">
          <p>Need help? Contact <a href="mailto:support@shirosec.com" className="text-cyan-400 hover:underline">support@shirosec.com</a></p>
        </div>
      </main>
    </div>
  );
};

export default AgentDownload;
